// ============================================
// DOT ADVISOR VIEW
// Standalone FMCSA/DOT compliance Q&A chatbot.
// Uses the dot-guru knowledge base.
// ============================================

const DOT_ADVISOR_SUGGESTIONS = [
  { icon: 'medical_services', q: 'Does my non-CDL driver need a med card?' },
  { icon: 'badge',            q: 'What triggers a CDL Class A requirement?' },
  { icon: 'schedule',         q: 'What is the oilfield HOS exception?' },
  { icon: 'description',      q: 'How long must I keep DVIR records?' },
];

let dotAdvisorHistory = [];
// TASK-9301 — mirrors ASSISTANT_INPUT_MAX_CHARS in functions/src/index.js (tripwire-checked).
const DOT_ADVISOR_MAX_QUESTION_CHARS = 5000;

export function initDotAdvisor(container, tenant) {
  container.innerHTML = renderDotAdvisorView();

  const input = document.getElementById('dot-advisor-input');
  const sendBtn = document.getElementById('dot-advisor-send');
  const resetBtn = document.getElementById('dot-advisor-reset');
  const suggestions = document.getElementById('dot-advisor-suggestions');

  if (sendBtn) sendBtn.addEventListener('click', sendDotAdvisorMessage);
  if (resetBtn) resetBtn.addEventListener('click', resetDotAdvisorConversation);
  if (input) {
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendDotAdvisorMessage();
      }
    });
    input.addEventListener('input', autosizeInput);
  }
  if (suggestions) {
    suggestions.addEventListener('click', (e) => {
      const chip = e.target.closest('[data-question]');
      if (!chip) return;
      selectSuggestion(chip.getAttribute('data-question'));
    });
  }

  renderDotAdvisorSuggestions();

  return null;
}

function renderDotAdvisorView() {
  return `
    <div class="dot-advisor-page">
      <header class="dot-advisor-header">
        <div class="dot-advisor-header-row">
          <div class="dot-advisor-brand">
            <div class="dot-advisor-brand-icon"><span class="material-icons">policy</span></div>
            <div class="dot-advisor-brand-text">
              <h2>DOT Advisor</h2>
              <p>FMCSA regulatory guidance — 49 CFR expertise on demand</p>
            </div>
          </div>
          <div class="dot-advisor-header-actions">
            <div class="dot-advisor-badges" aria-hidden="true">
              <span class="dot-advisor-badge"><span class="material-icons">verified</span>Cites CFR</span>
              <span class="dot-advisor-badge"><span class="material-icons">fact_check</span>Always verify</span>
            </div>
            <button type="button" class="dot-advisor-reset-btn" id="dot-advisor-reset" title="Start a new conversation" aria-label="New conversation">
              <span class="material-icons">refresh</span>
              <span class="dot-advisor-reset-label">New chat</span>
            </button>
          </div>
        </div>
        <div class="dot-advisor-disclaimer" role="note">
          <span class="material-icons" aria-hidden="true">gavel</span>
          <span><strong>Guidance only — not legal advice.</strong> Verify with a qualified compliance professional and the official CFR before acting.</span>
        </div>
      </header>

      <section class="dot-advisor-chat-wrap">
        <div class="dot-advisor-chat" id="dot-advisor-chat-window" role="log" aria-live="polite" aria-atomic="false">
          ${renderIntroMessage()}
        </div>

        <div class="dot-advisor-dock">
          <div class="dot-advisor-suggestions" id="dot-advisor-suggestions"></div>

          <div class="dot-advisor-input-area">
            <textarea id="dot-advisor-input" data-maxlen="50000" rows="1"
              placeholder="Ask about CDL, HOS, DVIR, hazmat, medical cards..."
              aria-label="Ask a DOT compliance question"></textarea>
            <button class="btn btn-primary dot-advisor-send" id="dot-advisor-send" aria-label="Send question">
              <span class="material-icons">send</span>
            </button>
          </div>
          <div class="dot-advisor-input-hint">Enter to send · Shift+Enter for a new line</div>
          <div class="dot-advisor-input-hint dot-advisor-transcript-notice" role="note">${escapeHtmlAdvisor(
            // TASK-9268 slice 3 — askDotAdvisor records a carrier member's questions.
            // Same copy as the other recorded assistants: it lives in app.js
            // (window.aiTranscriptNoticeText), read via window, never imported
            // (Pitfall #1). Fallback text if app.js has not defined it yet.
            typeof window.aiTranscriptNoticeText === 'function'
              ? window.aiTranscriptNoticeText()
              : 'Questions you ask here are saved for 90 days. Your account owner can review them.'
          )}</div>
        </div>
      </section>
    </div>
  `;
}

function renderIntroMessage() {
  return `
    <div class="ai-message ai-intro">
      <div class="ai-avatar dot-advisor-avatar"><span class="material-icons">policy</span></div>
      <div class="ai-bubble">
        <strong>Hi — I'm your DOT Advisor.</strong> Ask me about CDL requirements, endorsements, hours of service, DQ files, medical cards, DVIR rules, drug testing, permits, and more. I'll cite the CFR section with every answer.
      </div>
    </div>
  `;
}

function renderDotAdvisorSuggestions() {
  const container = document.getElementById('dot-advisor-suggestions');
  if (!container) return;

  const chips = DOT_ADVISOR_SUGGESTIONS.map(s => `
    <button type="button" class="dot-advisor-chip" data-question="${escapeAttr(s.q)}">
      <span class="material-icons" aria-hidden="true">${s.icon}</span>
      <span>${escapeHtmlAdvisor(s.q)}</span>
    </button>
  `).join('');

  container.innerHTML = `
    <div class="dot-advisor-suggestions-label">Start with a common question</div>
    <div class="dot-advisor-chip-row">${chips}</div>
  `;
}

export function selectSuggestion(question) {
  const input = document.getElementById('dot-advisor-input');
  if (input) {
    input.value = question;
    input.focus();
    autosizeInput({ target: input });
  }
  const suggestions = document.getElementById('dot-advisor-suggestions');
  if (suggestions) suggestions.innerHTML = '';
}

function resetDotAdvisorConversation() {
  dotAdvisorHistory = [];
  const chatWindow = document.getElementById('dot-advisor-chat-window');
  if (chatWindow) chatWindow.innerHTML = renderIntroMessage();
  renderDotAdvisorSuggestions();
  const input = document.getElementById('dot-advisor-input');
  if (input) {
    input.value = '';
    autosizeInput({ target: input });
    input.focus();
  }
}

function autosizeInput(e) {
  const el = e?.target;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, 140) + 'px';
}

async function sendDotAdvisorMessage() {
  const input = document.getElementById('dot-advisor-input');
  const chatWindow = document.getElementById('dot-advisor-chat-window');
  const sendBtn = document.getElementById('dot-advisor-send');

  const question = input?.value?.trim();
  if (!question) return;
  if (question.length > DOT_ADVISOR_MAX_QUESTION_CHARS) {   // TASK-9301 — told plainly, nothing sent, text kept
    window.showToast?.(`Your message is ${Number(question.length).toLocaleString('en-US')} characters — this assistant takes up to ${Number(DOT_ADVISOR_MAX_QUESTION_CHARS).toLocaleString('en-US')}. Nothing was sent. Shorten it or split it into two messages.`, 'error', null, 12000);
    return;
  }

  const suggestions = document.getElementById('dot-advisor-suggestions');
  if (suggestions) suggestions.innerHTML = '';

  const userMsg = document.createElement('div');
  userMsg.className = 'user-message';
  userMsg.innerHTML = `
    <div class="user-bubble">${escapeHtmlAdvisor(question)}</div>
    <div class="user-avatar"><span class="material-icons">person</span></div>
  `;
  chatWindow.appendChild(userMsg);

  const thinkingMsg = document.createElement('div');
  thinkingMsg.className = 'ai-message ai-thinking';
  thinkingMsg.innerHTML = `
    <div class="ai-avatar dot-advisor-avatar"><span class="material-icons">policy</span></div>
    <div class="ai-bubble">
      <span class="thinking-dots"><span>.</span><span>.</span><span>.</span></span>
      <span class="dot-advisor-thinking-label">Checking the regs...</span>
    </div>
  `;
  chatWindow.appendChild(thinkingMsg);
  chatWindow.scrollTop = chatWindow.scrollHeight;

  input.value = '';
  autosizeInput({ target: input });
  if (sendBtn) sendBtn.disabled = true;

  dotAdvisorHistory.push({ role: 'user', content: question });

  try {
    const token = await window.AppState?.user?.getIdToken?.();
    if (!token) throw new Error('Not authenticated');

    const response = await fetch('https://us-central1-dispatch-deck-be88c.cloudfunctions.net/askDotAdvisor', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({
        data: {
          question,
          history: dotAdvisorHistory.slice(-20),
          tenantId: window.AppState?.tenant || null
        }
      })
    });

    let result;
    try {
      result = await response.json();
    } catch (parseErr) {
      throw new Error(`Server returned ${response.status} (non-JSON response)`);
    }
    thinkingMsg.remove();

    const answer = result.result?.answer || result.data?.answer;
    if (answer) {
      dotAdvisorHistory.push({ role: 'assistant', content: answer });

      const aiMsg = document.createElement('div');
      aiMsg.className = 'ai-message';
      aiMsg.innerHTML = `
        <div class="ai-avatar dot-advisor-avatar"><span class="material-icons">policy</span></div>
        <div class="ai-bubble dot-advisor-answer">${formatDotAdvisorAnswer(answer)}</div>
      `;
      chatWindow.appendChild(aiMsg);
    } else {
      const serverError = result.error || result.result?.error || `HTTP ${response.status}`;
      throw new Error(serverError);
    }
  } catch (err) {
    console.error('[DOT Advisor] Error:', err);
    thinkingMsg.remove();

    let userMessage;
    const msg = String(err?.message || '');
    if (msg === 'Not authenticated' || msg.includes('401') || msg.toLowerCase().includes('unauthorized')) {
      userMessage = "You're signed out. Refresh the page and sign in again.";
    } else if (msg.includes('429') || msg.toLowerCase().includes('limit reached')) {
      userMessage = "You've hit today's AI usage limit. Try again tomorrow or contact support.";
    } else if (msg.includes('User not found') || msg.includes('403')) {
      userMessage = "Your account isn't linked to a tenant. Contact support.";
    } else if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
      userMessage = "Couldn't reach the DOT Advisor. Check your connection and try again.";
    } else {
      userMessage = `DOT Advisor error: ${escapeHtmlAdvisor(msg)}`;
    }

    const errMsg = document.createElement('div');
    errMsg.className = 'ai-message ai-error';
    errMsg.innerHTML = `
      <div class="ai-avatar dot-advisor-avatar"><span class="material-icons">policy</span></div>
      <div class="ai-bubble dot-advisor-answer dot-advisor-answer-error">
        ${userMessage}
      </div>
    `;
    chatWindow.appendChild(errMsg);
    dotAdvisorHistory.pop();
  } finally {
    if (sendBtn) sendBtn.disabled = false;
    chatWindow.scrollTop = chatWindow.scrollHeight;
  }
}

function formatDotAdvisorAnswer(text) {
  let html = escapeHtmlAdvisor(text);
  // Link out CFR citations (e.g. "49 CFR 391.23", "49 CFR Part 391") to eCFR.
  html = html.replace(
    /\b(49)\s*CFR\s*(?:Part\s*)?(\d+)(?:\.(\d+))?/gi,
    (match, title, part, section) => {
      const path = section
        ? `title-${title}/part-${part}/section-${part}.${section}`
        : `title-${title}/part-${part}`;
      const url = `https://www.ecfr.gov/current/${path}`;
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="dot-advisor-cite">${match}</a>`;
    }
  );
  // Inline section references like "§ 391.23"
  html = html.replace(
    /§\s*(\d+)\.(\d+)/g,
    (match, part, section) => {
      const url = `https://www.ecfr.gov/current/title-49/part-${part}/section-${part}.${section}`;
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="dot-advisor-cite">${match}</a>`;
    }
  );
  html = html.replace(/\n/g, '<br>');
  return html;
}

function escapeHtmlAdvisor(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function escapeAttr(str) {
  return escapeHtmlAdvisor(str);
}

window.DotAdvisorModule = { selectSuggestion, init: initDotAdvisor };
